/**
 * useBuilderCatalog — the one catalog + pricing-inputs loader, shared by the
 * quote builder and the change-order builder.
 *
 * IDI-457 (parent IDI-455, Adrian 2026-10-06: "Change orders are supposed to
 * use the same UX, layouts, logic and components ... as the quote builder").
 *
 * Before this, `QuoteBuilderPage.loadAccessoryCatalog` was the only place that
 * loaded the real pricebook, and `ChangeOrderBuilderPage` passed none of it to
 * `AddItemCascade` — so a change order offered the ~25 static sample products
 * and priced lines with a different margin cascade than the quote it amends.
 *
 * WHAT THIS OWNS (shared, both builders)
 *   - the pricebook + tenant-overlay merge, and the two offerable subsets
 *   - categoryMargins / laborDefaults / laborBurdenPct / customerTypes
 *
 * WHAT THIS DELIBERATELY DOES NOT OWN (quote-only seeding)
 *   - `master_default_margin` → overallMargin. This is gated on
 *     `!isEditing && !isTemplateEdit` because of IDI-47: Settings is cached
 *     and fast, the quote fetch is slower, so on an edit page applying the
 *     Settings default would overwrite the quote's own loaded margin. 61
 *     quotes were contaminated live by exactly that race. A change order has
 *     no equivalent seed, so this stays in QuoteBuilderPage.
 *   - the new-quote tax-rate seed, payment defaults, and the `?customerId`
 *     prefill — all quote-creation concerns.
 * The hook returns the raw `settings` object so each builder can do its own
 * seeding from the same single fetch (no second network round-trip).
 *
 * Guards carried over verbatim — each was earned from a live incident:
 *   - IDI-208/IDI-136: `settings.quote_items` is a tenant-edit OVERLAY, not
 *     the catalog. Reading it alone surfaced 3 rows on tenant 270 instead of
 *     the ~7,540-row pricebook. The merge is `buildCatalogRows`.
 *   - IDI-373 (ruling 2026-09-24): offer only products that can produce a
 *     correct line total — `filterAreaPriceableRows` /
 *     `filterOfferableAccessoryRows`. A $0 dealer price means NOT SET.
 *   - IDI-379: Settings stores customer types under `customerTypes`
 *     (camelCase, confirmed live on tenant 1; `customer_types` ABSENT).
 *   - IDI-205: labor burden % is hydrated unconditionally — it's a live
 *     account-wide setting, not a one-time seed.
 *   - IDI-347: `settingsHydrated` flips in `finally`, so a settings fetch
 *     that 500s can't wedge autosave off for the whole session.
 */
import { useCallback, useEffect, useState } from 'react';
import { settingsAPI } from '../services/api';
import {
  buildCatalogRows,
  filterAreaPriceableRows,
  filterOfferableAccessoryRows,
} from '../quote-builder/catalogRows';

const EMPTY_CATALOG = Object.freeze({
  catalogRows: [],
  accessoryCatalog: [],
  insulationCatalog: [],
  categoryMargins: null,
  laborDefaults: null,
  laborBurdenPct: 0,
  customerTypes: [],
  settings: null,
});

export default function useBuilderCatalog() {
  const [data, setData] = useState(EMPTY_CATALOG);
  // `loaded` gates the cascade's Add button (catalogLoading) and pins the
  // autosave dirty baseline. It must become true even on failure.
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pricebook, settings] = await Promise.all([
        settingsAPI.getList('pricebook'),
        settingsAPI.getSettings(),
      ]);
      const overlays = Array.isArray(settings?.quote_items) ? settings.quote_items : [];
      const items = buildCatalogRows(pricebook, overlays);

      const md = settings?.marginDefaults;
      const categoryMargins = md?.category_margins && typeof md.category_margins === 'object'
        ? md.category_margins
        : null;

      const lr = md?.labor_rates || settings?.labor_rates;
      const laborDefaults = lr && typeof lr === 'object' ? lr : null;

      // IDI-205: unconditional, and only a positive number counts.
      // IDI-848: Quote Defaults' Save stores `quoteDefaults` (a string, "10").
      // A present key wins, even "0" or empty; older keys are fallbacks.
      const qd = settings?.quoteDefaults;
      const rawBurden = Number(
        qd && typeof qd === 'object' && 'labor_burden_pct' in qd
          ? qd.labor_burden_pct
          : (settings?.quote_defaults?.labor_burden_pct ?? settings?.labor_burden_pct ?? 0),
      );
      const laborBurdenPct = Number.isFinite(rawBurden) && rawBurden > 0 ? rawBurden : 0;

      // IDI-379: camelCase first, snake_case fallback for older tenants.
      const ct = settings?.customerTypes ?? settings?.customer_types;

      setData({
        catalogRows: items,
        accessoryCatalog: filterOfferableAccessoryRows(items),
        insulationCatalog: filterAreaPriceableRows(items),
        categoryMargins,
        laborDefaults,
        laborBurdenPct,
        customerTypes: Array.isArray(ct) ? ct : [],
        settings: settings || null,
      });
    } catch {
      // Silent: the picker shows its empty state. Never throw here — a
      // settings outage must not blank the builder.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A tenant can author a custom accessory from inside the builder. It is
  // persisted to Settings by the caller; this shows it immediately without
  // re-fetching the whole ~7,540-row catalog.
  const appendAccessory = useCallback((row) => {
    if (!row) return;
    setData((prev) => ({
      ...prev,
      accessoryCatalog: [...prev.accessoryCatalog, row],
      catalogRows: [...prev.catalogRows, row],
    }));
  }, []);

  return {
    ...data,
    catalogLoaded: loaded,
    appendAccessory,
    // Exposed for the "custom accessory created" refresh path.
    reload: load,
  };
}
